// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CartTotalDisplayStyles } from "@bg/Overrides/Components/CartTotalDisplay";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay from "@insite/content-library/Components/CartTotalDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotions = cartId ? getPromotionsDataView(state, cartId).value : getCurrentPromotionsDataView(state).value;
    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotions) {
        orderPromotions = getOrderPromotions(promotions);
        shippingPromotions = getShippingPromotions(promotions);
        discountTotal = getDiscountTotal(promotions);
    }
    return {
        cart: cartState.value,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        isLoading: !cartState.value || cartState.isLoading,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutOrderSummaryStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    editGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
}

export const orderSummaryStyles: OnePageCheckoutOrderSummaryStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    editGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    cartTotalGridItem: { width: 12 },
    cartTotal: {
        container: {
            gap: 10,
            css: css`
                padding: 0;
                background-color: transparent;
            `,
        },
    },
};

const styles = orderSummaryStyles;

const OnePageCheckoutOrderSummary = ({
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    cartUrl,
}: Props) => {
    // XNG-Change
    // if (isLoading) {
    //     return (
    //         <StyledWrapper {...styles.centeringWrapper}>
    //             <LoadingSpinner data-test-selector="onePageCheckout_orderSummary" />
    //         </StyledWrapper>
    //     );
    // }

    return (
        <CartTotalDisplay
            extendedStyles={styles.cartTotal}
            cart={cart}
            orderPromotions={orderPromotions}
            shippingPromotions={shippingPromotions}
            discountTotal={discountTotal}
            // XNG-Changes
            {...{ showTaxMessage: true }}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutOrderSummary),
    definition: {
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
        displayName: "Order Summary",
    },
};

export default widgetModule;
